import appleLogo from "@assets/apple_logo.webp"
import googleLogo from "@assets/google_logo.webp"
import Button from "@components/Button"
import Input from "@components/Input"
import PasswordInput from "@components/PasswordInput"
import FunnelHeader from "@components/quiz/FunnelHeader"
import FunnelLayout from "@components/quiz/FunnelLayout"
import { canGoBack } from "@helpers/history"
import { signInWithOAuth } from "@helpers/supabase"
import { useTranslation } from "react-i18next"
import {
  Form,
  Link,
  useActionData,
  useNavigate,
  useNavigation,
} from "react-router-dom"
import { SignInActionData, SignInActionError } from "src/actions/signInAction"

export default function SignInPage() {
  const navigate = useNavigate()
  const navigation = useNavigation()
  const signInActionData = useActionData() as SignInActionData
  const { t } = useTranslation()

  const error = signInActionData?.error

  const isSubmitting = ["loading", "submitting"].includes(navigation.state)

  const handleSignInWith = async (provider: "apple" | "google") => {
    await signInWithOAuth({ provider })
  }

  const translateError = (error: SignInActionError) => {
    switch (error.code) {
      case "invalid_credentials":
        return t("pages.account.signIn.invalidCredentials")
      case "validation_failed":
        return t("pages.account.signIn.invalidEmail")
      case "missing_password":
        return t("pages.account.signIn.enterPassword")
      default:
        return error.message
    }
  }

  const isEmailInvalid =
    error?.code === "invalid_credentials" || error?.code === "validation_failed"
  const isPasswordInvalid =
    error?.code === "invalid_credentials" || error?.code === "missing_password"

  return (
    <FunnelLayout
      header={
        <FunnelHeader onBack={canGoBack() ? () => navigate(-1) : undefined} />
      }
    >
      <div className="flex grow flex-col gap-6 pb-8 pt-4">
        <div className="animate-fade-up space-y-3">
          <h1 className="vm-heading">{t("pages.account.signIn.heading")}</h1>
          <p className="vm-subheading">
            {t("pages.account.signIn.subheading")}
          </p>
        </div>

        <Form
          className="animate-fade-up space-y-4 [animation-delay:120ms]"
          method="post"
        >
          <fieldset className="space-y-3" disabled={isSubmitting}>
            <Input
              autoComplete="email"
              inputMode="email"
              invalid={isEmailInvalid}
              name="email"
              placeholder={t("shared.email")}
              type="email"
            />

            <PasswordInput
              autoComplete="current-password"
              invalid={isPasswordInvalid}
              name="password"
            />
          </fieldset>

          {error && (
            <p
              className="animate-fade-in rounded-vm-md bg-vm-red-soft p-3 text-center text-sm font-semibold text-vm-red-text"
              role="alert"
            >
              {translateError(error)}
            </p>
          )}

          <div className="flex justify-end">
            <Link
              className="rounded text-sm font-semibold text-vm-primary-text underline-offset-4 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-vm-primary/40"
              to="/account/forgot-password"
            >
              {t("pages.account.signIn.forgotPassword")}
            </Link>
          </div>

          <Button
            submitting={isSubmitting}
            title={t("shared.signIn")}
            type="submit"
          />
        </Form>

        <div className="flex animate-fade-up items-center gap-3 text-xs font-semibold uppercase tracking-wide text-vm-muted [animation-delay:200ms]">
          <span className="h-px grow bg-vm-line" />
          {t("pages.account.signIn.or")}
          <span className="h-px grow bg-vm-line" />
        </div>

        <div className="animate-fade-up space-y-3 [animation-delay:240ms]">
          <button
            className="vm-button-secondary"
            onClick={() => void handleSignInWith("apple")}
            type="button"
          >
            {/* NOTE: The asset is white, made for the old dark theme. */}
            <img
              alt=""
              className="h-5 w-5 invert dark:invert-0"
              src={appleLogo}
            />
            {t("pages.account.signIn.signInWithApple")}
          </button>

          <button
            className="vm-button-secondary"
            onClick={() => void handleSignInWith("google")}
            type="button"
          >
            <img alt="" className="h-5 w-5" src={googleLogo} />
            {t("pages.account.signIn.signInWithGoogle")}
          </button>
        </div>

        <p className="mt-auto pt-2 text-center text-sm">
          <Link
            className="font-semibold text-vm-ink underline underline-offset-4 hover:text-vm-primary-text"
            to="/onboarding"
          >
            {t("pages.account.signIn.signUp")}
          </Link>
        </p>
      </div>
    </FunnelLayout>
  )
}
